import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calendar, Target, Users, TrendingUp, MessageSquare, PenLine, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useConversationFieldSettings } from "@/hooks/useConversationFieldSettings";

interface EmployeeConversationViewProps {
  conversationId: string;
  employeeName: string;
  onClose: () => void;
}

const conversationTypeLabels: Record<string, string> = {
  jahresgespraech: "Jahresgespräch",
  followup: "Follow-up-Gespräch",
  sonstiges: "Sonstiges",
};

// Block icons mapping
const blockIcons: Record<string, React.ReactNode> = {
  block1: <TrendingUp className="h-5 w-5 text-primary" />,
  block2: <Target className="h-5 w-5 text-primary" />,
  block3: <Users className="h-5 w-5 text-primary" />,
  block4: <TrendingUp className="h-5 w-5 text-primary" />,
  block5: <Target className="h-5 w-5 text-primary" />,
};

export function EmployeeConversationView({
  conversationId,
  employeeName,
  onClose,
}: EmployeeConversationViewProps) {
  const { blocks, loading: blocksLoading } = useConversationFieldSettings();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      const { data: conv, error } = await supabase
        .from("employee_conversations")
        .select("*")
        .eq("id", conversationId)
        .single();

      if (error) throw error;
      setData(conv);
    } catch (error: any) {
      console.error("Error loading conversation:", error);
      toast.error("Fehler beim Laden des Gesprächs");
    } finally {
      setLoading(false);
    }
  };

  if (loading || blocksLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Gespräch nicht gefunden
      </div>
    );
  }

  const renderField = (label: string, value: string | null) => {
    if (!value) return null;
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="text-sm whitespace-pre-wrap">{value}</p>
      </div>
    );
  };

  // Check if a block has any content
  const blockHasContent = (block: typeof blocks[0]): boolean => {
    return block.fields.some((field) => data[field.key]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {conversationTypeLabels[data.conversation_type]} - {employeeName}
            </CardTitle>
            <Badge 
              variant={data.status === "abgeschlossen" ? "default" : "secondary"} 
              className={data.status === "abgeschlossen" ? "bg-green-600" : ""}
            >
              {data.status === "abgeschlossen" ? "Abgeschlossen" : "Entwurf"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Gesprächsdatum</p>
            <p className="font-medium">
              {format(new Date(data.conversation_date), "dd.MM.yyyy", { locale: de })}
            </p>
          </div>
          {data.follow_up_date && (
            <div>
              <p className="text-sm text-muted-foreground">Follow-up-Termin</p>
              <p className="font-medium">
                {format(new Date(data.follow_up_date), "dd.MM.yyyy", { locale: de })}
              </p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground">Erstellt am</p>
            <p className="font-medium">
              {format(new Date(data.created_at), "dd.MM.yyyy HH:mm", { locale: de })}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Content Blocks from Settings */}
      {blocks.map((block) => {
        if (!blockHasContent(block)) return null;

        return (
          <Card key={block.block_id}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {blockIcons[block.block_id] || <MessageSquare className="h-5 w-5 text-primary" />}
                {block.block_label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {block.fields.map((field) => renderField(field.label, data[field.key]))}
            </CardContent>
          </Card>
        );
      })}

      {/* Besondere Bemerkungen */}
      {data.besondere_bemerkungen && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-5 w-5" />
              Besondere Bemerkungen / Sonstiges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{data.besondere_bemerkungen}</p>
          </CardContent>
        </Card>
      )}


      {/* Unterschriften */}
      {(data.supervisor_signature_url || data.employee_signature_url) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PenLine className="h-5 w-5" />
              Unterschriften
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.supervisor_signature_url && (
              <div className="border rounded-lg p-4 bg-green-500/5 border-green-500/30">
                <div className="flex items-center gap-2 mb-2 text-green-600">
                  <Check className="h-4 w-4" />
                  <span className="font-medium">Vorgesetzter</span>
                </div>
                <img 
                  src={data.supervisor_signature_url} 
                  alt="Unterschrift Vorgesetzter" 
                  className="max-h-[100px] bg-white rounded" 
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {data.supervisor_name} - {data.supervisor_signed_at && 
                    format(new Date(data.supervisor_signed_at), "dd.MM.yyyy HH:mm", { locale: de })}
                </p>
              </div>
            )}
            {data.employee_signature_url && (
              <div className="border rounded-lg p-4 bg-green-500/5 border-green-500/30">
                <div className="flex items-center gap-2 mb-2 text-green-600">
                  <Check className="h-4 w-4" />
                  <span className="font-medium">Mitarbeiter</span>
                </div>
                <img 
                  src={data.employee_signature_url} 
                  alt="Unterschrift Mitarbeiter" 
                  className="max-h-[100px] bg-white rounded" 
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {data.employee_signed_name} - {data.employee_signed_at && 
                    format(new Date(data.employee_signed_at), "dd.MM.yyyy HH:mm", { locale: de })}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
